export const volumeMeter = (stream, setVolumeLevel, existingAudioContext = null, options = {}) => {
  // Reuse existing AudioContext if provided to prevent Chrome conflicts
  const audioContext = existingAudioContext || new (window.AudioContext || window['webkitAudioContext'])();
  const analyser = audioContext.createAnalyser();
  const source = audioContext.createMediaStreamSource(stream);
  source.connect(analyser);
  analyser.fftSize = 256;
  // Use time-domain data for accurate RMS volume measurement (not frequency spectrum)
  const dataArray = new Uint8Array(analyser.fftSize);

  // Basic silence detection options
  const {
    enableSilenceDetection = false,
    silenceThreshold = 1, // Volume level below which is considered silence
    sessionId = null
  } = options;

  let silenceStartTime = null;
  let isSilent = false;
  let currentRmsVolume = 0; // Store for getVolumeInfo

  const updateVolume = () => {
    // Use time-domain waveform data (not frequency spectrum) for accurate speech detection
    // getByteTimeDomainData returns samples centered at 128 (0 = -1.0, 128 = 0.0, 255 = +1.0)
    analyser.getByteTimeDomainData(dataArray);
    
    // Calculate RMS (root mean square) - the standard measure for perceived loudness
    let sumSquares = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const normalized = (dataArray[i] - 128) / 128; // Convert to -1.0 to +1.0
      sumSquares += normalized * normalized;
    }
    const rms = Math.sqrt(sumSquares / dataArray.length);
    
    // Map RMS to 0-90 scale for consistency with getVolumeInfo consumers (they divide by 10)
    // Typical RMS values: silence < 0.01, quiet speech 0.05-0.1, normal speech 0.1-0.3, loud 0.3-0.5
    // Scale factor 300 maps: 0.01→3 (level 0), 0.1→30 (level 3), 0.2→60 (level 6), 0.3→90 (level 9)
    const volume = Math.min(90, rms * 300);
    currentRmsVolume = volume; // Store for getVolumeInfo
    const volumeLevel = Math.min(9, Math.round(volume / 10));

    setVolumeLevel(volumeLevel);

    // Basic silence detection
    if (enableSilenceDetection) {
      const currentlyQuiet = volumeLevel <= silenceThreshold;
      
      if (currentlyQuiet && !isSilent) {
        // Just became silent
        silenceStartTime = Date.now();
        isSilent = true;
      } else if (!currentlyQuiet && isSilent) {
        // Just stopped being silent
        const silenceDuration = Date.now() - silenceStartTime;
        isSilent = false;
        silenceStartTime = null;
      }
    }

    requestAnimationFrame(updateVolume);
  };
  
  updateVolume();

  // Return basic volume info and cleanup function
  return {
    cleanup: () => {
      source.disconnect();
      if (audioContext.state !== 'closed') {
        audioContext.close();
      }
    },
    getVolumeInfo: () => ({
      isSilent,
      silenceStartTime,
      currentVolume: currentRmsVolume
    })
  };
};

export const convertFloat32ToLinear16 = (input) => {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return output;
};

export const fetchAudioInputDevices = async (
  setAudioInputDevices,
  setSelectedDeviceId
) => {
  try {
    // Request microphone permission first to ensure we get device labels
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
    } catch (permissionError) {
      console.warn("Microphone permission denied or unavailable:", permissionError);
    }

    // Now enumerate devices
    const devices = await navigator.mediaDevices.enumerateDevices();
    let audioDevices = devices.filter(
      (device) => device.kind === "audioinput"
    );

    // Deduplicate devices based on label patterns
    const uniqueDevices = [];
    const seenLabels = new Map();
    
    audioDevices.forEach(device => {
      // Extract the core device name without additional identifiers
      let coreLabel = device.label || `Microphone ${device.deviceId}`;
      
      // Remove common suffixes like (Default), (Communications), numbers, etc.
      const cleanLabel = coreLabel
        .replace(/\s*\(.*?\)\s*/g, '') // Remove anything in parentheses
        .replace(/\s*-\s*.*$/, '') // Remove anything after dash
        .replace(/\s+\d+$/, '') // Remove trailing numbers
        .replace(/\s*Default\s*/gi, '') // Remove "Default" text
        .replace(/\s*Communications\s*/gi, '') // Remove "Communications" text
        .replace(/\s*\[\d+\]\s*/g, '') // Remove array indices like [0], [1]
        .replace(/\s*#\d+\s*/g, '') // Remove hash numbers like #1, #2
        .trim();
      
      // Only add if we haven't seen this core label
      if (cleanLabel && device.deviceId) {
        if (!seenLabels.has(cleanLabel)) {
          seenLabels.set(cleanLabel, device);
          uniqueDevices.push(device);
        }
      }
    });
    
    setAudioInputDevices(uniqueDevices);

    if (uniqueDevices.length > 0) {
      setSelectedDeviceId(uniqueDevices[0].deviceId);
    } else {
      console.warn("No audio input devices found.");
    }
  } catch (error) {
    console.error("Error fetching audio input devices:", error);
  }
};

export const handleMicChange = async (
  deviceId,
  setSelectedDeviceId,
  updateUserSettings,
  audioInputDevices,
  userId
) => {
  let stream;
  setSelectedDeviceId(deviceId);
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: deviceId } } });

    const devices = audioInputDevices.map((device) => ({
      deviceId: device.deviceId,
      label: device.label,
    }));
    await updateUserSettings(userId, {
      microphone: deviceId,
      audioDevices: devices,
    });
  } catch (error) {
    console.error("Error updating user settings:", error);
  } finally {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  }
};